import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Save, Ruler, ChevronRight, AlertTriangle, Check } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Adjustment options for dropdowns
const ADJUSTMENT_OPTIONS = [
  { value: -2, label: "-2" }, { value: -1.75, label: "-1 3/4" }, { value: -1.5, label: "-1 1/2" },
  { value: -1.25, label: "-1 1/4" }, { value: -1, label: "-1" }, { value: -0.875, label: "-7/8" },
  { value: -0.75, label: "-3/4" }, { value: -0.625, label: "-5/8" }, { value: -0.5, label: "-1/2" },
  { value: -0.375, label: "-3/8" }, { value: -0.25, label: "-1/4" }, { value: -0.125, label: "-1/8" },
  { value: 0, label: "0" },
  { value: 0.125, label: "+1/8" }, { value: 0.25, label: "+1/4" }, { value: 0.375, label: "+3/8" },
  { value: 0.5, label: "+1/2" }, { value: 0.625, label: "+5/8" }, { value: 0.75, label: "+3/4" },
  { value: 0.875, label: "+7/8" }, { value: 1, label: "+1" }, { value: 1.25, label: "+1 1/4" },
  { value: 1.5, label: "+1 1/2" }, { value: 1.75, label: "+1 3/4" }, { value: 2, label: "+2" },
];

const POSTURE_OPTIONS = [
  { value: -2, label: "-2" }, { value: -1.5, label: "-1 1/2" }, { value: -1, label: "-1" },
  { value: -0.75, label: "-3/4" }, { value: -0.5, label: "-1/2" }, { value: -0.25, label: "-1/4" },
  { value: 0, label: "0" },
  { value: 0.25, label: "+1/4" }, { value: 0.5, label: "+1/2" }, { value: 0.75, label: "+3/4" },
  { value: 1, label: "+1" }, { value: 1.5, label: "+1 1/2" }, { value: 2, label: "+2" },
];

const PANT_MODEL_TYPES = ["Pleated", "Flat Front"];

// Fit Preference Options
const FIT_PREFERENCES = {
  chest: [
    { value: "slim", label: "Slim", code: "CS" },
    { value: "regular", label: "Regular", code: "CR" },
    { value: "full", label: "Full", code: "CF" }
  ],
  waistSuppression: [
    { value: "low", label: "Low (Relaxed)", code: "WL" },
    { value: "medium", label: "Medium (Standard)", code: "WM" },
    { value: "aggressive", label: "Aggressive (Shaped)", code: "WA" }
  ],
  shoulder: [
    { value: "natural", label: "Natural", code: "SN" },
    { value: "soft", label: "Soft", code: "SS" },
    { value: "structured", label: "Structured", code: "ST" }
  ],
  trouserBreak: [
    { value: "none", label: "No Break", code: "BN" },
    { value: "quarter", label: "Quarter Break", code: "BQ" },
    { value: "half", label: "Half Break", code: "BH" },
    { value: "full", label: "Full Break", code: "BF" }
  ]
};

// Measurement Row Component
const MeasurementRow = ({ label, fieldKey, startingSpecs, adjustments, setAdjustments }) => (
  <tr className="border-b border-white/5">
    <td className="py-2 px-3 text-sm">{label}</td>
    <td className="py-2 px-3 text-sm text-center font-mono">{startingSpecs[fieldKey] || "-"}</td>
    <td className="py-2 px-3">
      <Select value={adjustments[fieldKey]?.toString() || "0"} onValueChange={(v) => setAdjustments(prev => ({...prev, [fieldKey]: parseFloat(v)}))}>
        <SelectTrigger className="h-7 w-20 bg-background border-white/20 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {ADJUSTMENT_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </td>
    <td className="py-2 px-3 text-sm text-center font-mono text-primary font-medium">
      {((startingSpecs[fieldKey] || 0) + (adjustments[fieldKey] || 0)).toFixed(2)}
    </td>
  </tr>
);

// Posture Row Component
const PostureRow = ({ label, fieldKey, posture, setPosture }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <Select value={posture[fieldKey]?.toString() || "0"} onValueChange={(v) => setPosture(prev => ({...prev, [fieldKey]: parseFloat(v)}))}>
      <SelectTrigger className="h-6 w-16 bg-background border-white/20 text-[10px]"><SelectValue /></SelectTrigger>
      <SelectContent>
        {POSTURE_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value.toString()} className="text-xs">{opt.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const AdminMTMMeasurementsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Garment Type selection
  const [garmentType, setGarmentType] = useState("");
  
  // Garment selections
  const [includeJacket, setIncludeJacket] = useState(false);
  const [includeTrouser, setIncludeTrouser] = useState(false);
  const [includeVest, setIncludeVest] = useState(false);
  const [includeShirt, setIncludeShirt] = useState(false);
  
  // Size charts
  const [sizeCharts, setSizeCharts] = useState({ jacket: [], pant: [], vest: [], shirt: [] });
  
  // Jacket state
  const [jacketModelSize, setJacketModelSize] = useState("");
  const [jacketStartingSpecs, setJacketStartingSpecs] = useState({});
  const [jacketAdjustments, setJacketAdjustments] = useState({});
  const [jacketPosture, setJacketPosture] = useState({});
  const [jacketRemarks, setJacketRemarks] = useState("");
  
  // Pant state
  const [pantModelSize, setPantModelSize] = useState("");
  const [pantModelType, setPantModelType] = useState("Flat Front");
  const [pantStartingSpecs, setPantStartingSpecs] = useState({});
  const [pantAdjustments, setPantAdjustments] = useState({});
  const [pantPosture, setPantPosture] = useState({});
  const [pantRemarks, setPantRemarks] = useState("");
  
  // Vest state
  const [vestModelSize, setVestModelSize] = useState("");
  const [vestStartingSpecs, setVestStartingSpecs] = useState({});
  const [vestAdjustments, setVestAdjustments] = useState({});
  const [vestPosture, setVestPosture] = useState({});
  const [vestRemarks, setVestRemarks] = useState("");
  
  // Shirt state
  const [shirtModelSize, setShirtModelSize] = useState("");
  const [shirtStartingSpecs, setShirtStartingSpecs] = useState({});
  const [shirtAdjustments, setShirtAdjustments] = useState({});
  const [shirtRemarks, setShirtRemarks] = useState("");
  
  // Fit Preferences
  const [fitPreferences, setFitPreferences] = useState({
    chest: "regular",
    waistSuppression: "medium",
    shoulder: "natural",
    trouserBreak: "quarter"
  });
  
  // Body Posture Notes (structured)
  const [bodyPostureNotes, setBodyPostureNotes] = useState({
    shoulderSlope: "even",
    stance: "neutral",
    seatProminence: "normal"
  });
  
  // Active tab
  const [activeTab, setActiveTab] = useState("jacket");
  
  // Validation warnings
  const [warnings, setWarnings] = useState([]);

  // Handle garment type change - auto-select the right garments
  const handleGarmentTypeChange = (value) => {
    setGarmentType(value);
    
    // Reset all selections first
    let jacket = false;
    let trouser = false;
    let vest = false;
    let shirt = false;
    
    // Set selections based on garment type
    switch (value) {
      case "2-Piece Suit":
        jacket = true;
        trouser = true;
        break;
      case "3-Piece Suit":
        jacket = true;
        trouser = true;
        vest = true;
        break;
      case "Sports Coat":
      case "Blazer":
      case "Overcoat":
        jacket = true;
        break;
      case "Trousers":
        trouser = true;
        break;
      case "Vest":
        vest = true;
        break;
      case "Shirt":
        shirt = true;
        break;
      case "Tuxedo":
        jacket = true;
        trouser = true;
        break;
      default:
        break;
    }
    
    setIncludeJacket(jacket);
    setIncludeTrouser(trouser);
    setIncludeVest(vest);
    setIncludeShirt(shirt);
    
    // Update localStorage
    localStorage.setItem("mtm_garments", JSON.stringify({
      includeJacket: jacket,
      includeTrouser: trouser,
      includeVest: vest,
      includeShirt: shirt
    }));
    
    // Also save garment type to localStorage for other pages
    const generalInfo = JSON.parse(localStorage.getItem("mtm_general_info") || "{}");
    generalInfo.garmentType = value;
    localStorage.setItem("mtm_general_info", JSON.stringify(generalInfo));
    
    // Set active tab to first selected garment
    if (jacket) setActiveTab("jacket");
    else if (trouser) setActiveTab("trouser");
    else if (vest) setActiveTab("vest");
    else if (shirt) setActiveTab("shirt");
  };

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") { navigate("/admin"); return; }
    
    // Load garment type from localStorage
    const savedGeneralInfo = localStorage.getItem("mtm_general_info");
    if (savedGeneralInfo) {
      const info = JSON.parse(savedGeneralInfo);
      if (info.garmentType) {
        setGarmentType(info.garmentType);
      }
    }
    
    // Load garment selections from localStorage
    const savedGarments = localStorage.getItem("mtm_garments");
    if (savedGarments) {
      const garments = JSON.parse(savedGarments);
      setIncludeJacket(garments.includeJacket || false);
      setIncludeTrouser(garments.includeTrouser || false);
      setIncludeVest(garments.includeVest || false);
      setIncludeShirt(garments.includeShirt || false);
      
      // Set active tab to first selected garment
      if (garments.includeJacket) setActiveTab("jacket");
      else if (garments.includeTrouser) setActiveTab("trouser");
      else if (garments.includeVest) setActiveTab("vest");
      else if (garments.includeShirt) setActiveTab("shirt");
    }
    
    // Load existing measurements from localStorage
    const savedMeasurements = localStorage.getItem("mtm_measurements");
    if (savedMeasurements) {
      const m = JSON.parse(savedMeasurements);
      if (m.jacket) {
        setJacketModelSize(m.jacket.modelSize || "");
        setJacketStartingSpecs(m.jacket.startingSpecs || {});
        setJacketAdjustments(m.jacket.adjustments || {});
        setJacketPosture(m.jacket.posture || {});
        setJacketRemarks(m.jacket.remarks || "");
      }
      if (m.pant) {
        setPantModelSize(m.pant.modelSize || "");
        setPantModelType(m.pant.modelType || "Flat Front");
        setPantStartingSpecs(m.pant.startingSpecs || {});
        setPantAdjustments(m.pant.adjustments || {});
        setPantPosture(m.pant.posture || {});
        setPantRemarks(m.pant.remarks || "");
      }
      if (m.vest) {
        setVestModelSize(m.vest.modelSize || "");
        setVestStartingSpecs(m.vest.startingSpecs || {});
        setVestAdjustments(m.vest.adjustments || {});
        setVestPosture(m.vest.posture || {});
        setVestRemarks(m.vest.remarks || "");
      }
    }
    
    fetchSizeCharts();
  }, [navigate]);

  // Real-time validation
  useEffect(() => {
    const newWarnings = [];
    
    if (includeJacket && jacketStartingSpecs.chest) {
      const finalChest = (jacketStartingSpecs.chest || 0) + (jacketAdjustments.chest || 0);
      if (finalChest < 34 || finalChest > 60) {
        newWarnings.push({ section: "jacket", message: `Jacket chest ${finalChest}" seems unusual (typical: 34-60")` });
      }
    }
    
    if (includeTrouser && pantStartingSpecs.waist) {
      const finalWaist = (pantStartingSpecs.waist || 0) + (pantAdjustments.waist || 0);
      if (finalWaist < 26 || finalWaist > 52) {
        newWarnings.push({ section: "trouser", message: `Trouser waist ${finalWaist}" seems unusual (typical: 26-52")` });
      }
    }
    
    setWarnings(newWarnings);
  }, [includeJacket, includeTrouser, jacketStartingSpecs, jacketAdjustments, pantStartingSpecs, pantAdjustments]);

  const fetchSizeCharts = async () => {
    try {
      const res = await axios.get(`${API}/size-chart/all`);
      setSizeCharts(res.data);
    } catch (error) {
      console.error("Failed to fetch size charts:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyJacketSize = (size) => {
    setJacketModelSize(size);
    const chart = sizeCharts.jacket.find(s => s.size === size);
    if (chart) {
      setJacketStartingSpecs({
        chest: chart.chest, stomach: chart.stomach, seat: chart.seat,
        shoulder: chart.shoulder, bicep: chart.bicep, sleeve_length: chart.sleeve_length,
        back_length: chart.back_length, front_length: chart.front_length, cuff: chart.cuff
      });
      setJacketAdjustments({});
    }
  };

  const applyPantSize = (size) => {
    setPantModelSize(size);
    const chart = sizeCharts.pant.find(s => s.size === size);
    if (chart) {
      setPantStartingSpecs({
        waist: chart.waist, seat: chart.seat, thigh: chart.thigh,
        knee: chart.knee, pant_length: chart.pant_length, urise: chart.urise,
        bottom: chart.bottom || 16.5
      });
      setPantAdjustments({});
    }
  };

  const applyVestSize = (size) => {
    setVestModelSize(size);
    const chart = sizeCharts.vest?.find(s => s.size === size) || sizeCharts.jacket.find(s => s.size === size);
    if (chart) {
      setVestStartingSpecs({
        chest: chart.chest, stomach: chart.stomach, back_length: chart.back_length ? chart.back_length - 2 : 24
      });
      setVestAdjustments({});
    }
  };

  const handleSaveAndContinue = () => {
    setSaving(true);
    
    // Save measurements to localStorage
    const measurementsData = {
      jacket: includeJacket ? {
        modelSize: jacketModelSize,
        startingSpecs: jacketStartingSpecs,
        adjustments: jacketAdjustments,
        posture: jacketPosture,
        remarks: jacketRemarks
      } : null,
      pant: includeTrouser ? {
        modelSize: pantModelSize,
        modelType: pantModelType,
        startingSpecs: pantStartingSpecs,
        adjustments: pantAdjustments,
        posture: pantPosture,
        remarks: pantRemarks
      } : null,
      vest: includeVest ? {
        modelSize: vestModelSize,
        startingSpecs: vestStartingSpecs,
        adjustments: vestAdjustments,
        posture: vestPosture,
        remarks: vestRemarks
      } : null,
      shirt: includeShirt ? {
        modelSize: shirtModelSize,
        startingSpecs: shirtStartingSpecs,
        adjustments: shirtAdjustments,
        remarks: shirtRemarks
      } : null
    };
    
    localStorage.setItem("mtm_measurements", JSON.stringify(measurementsData));
    toast.success("Measurements saved!");
    setSaving(false);
    
    // Navigate to next step (Styles or Submit)
    navigate("/admin/mtm-styles");
  };

  // Calculate completion percentage
  const completionPercent = useMemo(() => {
    let total = 0;
    let complete = 0;
    
    if (includeJacket) {
      total += 2;
      if (jacketModelSize) complete++;
      if (Object.keys(jacketStartingSpecs).length > 0) complete++;
    }
    if (includeTrouser) {
      total += 2;
      if (pantModelSize) complete++;
      if (Object.keys(pantStartingSpecs).length > 0) complete++;
    }
    if (includeVest) {
      total += 2;
      if (vestModelSize) complete++;
      if (Object.keys(vestStartingSpecs).length > 0) complete++;
    }
    if (includeShirt) {
      total += 2;
      if (shirtModelSize) complete++;
      if (Object.keys(shirtStartingSpecs).length > 0) complete++;
    }
    
    return total > 0 ? Math.round((complete / total) * 100) : 0;
  }, [includeJacket, includeTrouser, includeVest, jacketModelSize, jacketStartingSpecs, pantModelSize, pantStartingSpecs, vestModelSize, vestStartingSpecs]);

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  }

  if (!includeJacket && !includeTrouser && !includeVest && !includeShirt) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
          <div className="flex items-center gap-3 mb-8">
            <Button variant="ghost" onClick={() => navigate("/admin/mtm-order")}>
              <ArrowLeft size={16} className="mr-2" /> Back to Order
            </Button>
          </div>
          <div className="flex items-center gap-3 mb-8">
            <Ruler className="text-primary" size={24} />
            <div>
              <h1 className="font-serif text-2xl">Measurements</h1>
              <p className="text-xs text-muted-foreground">Step 3 of 5 • Select garment type to begin</p>
            </div>
          </div>
          
          <div className="bg-card border border-white/10 rounded-lg p-6 max-w-md">
            <label className="text-sm font-medium block mb-2">Garment Type *</label>
            <p className="text-xs text-muted-foreground mb-4">Select a garment type to load the appropriate measurement sections</p>
            <Select value={garmentType} onValueChange={handleGarmentTypeChange}>
              <SelectTrigger className="bg-background border-white/20">
                <SelectValue placeholder="Select garment type..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2-Piece Suit">2-Piece Suit (Jacket + Trousers)</SelectItem>
                <SelectItem value="3-Piece Suit">3-Piece Suit (Jacket + Trousers + Vest)</SelectItem>
                <SelectItem value="Sports Coat">Sports Coat (Jacket Only)</SelectItem>
                <SelectItem value="Blazer">Blazer (Jacket Only)</SelectItem>
                <SelectItem value="Overcoat">Overcoat (Jacket Only)</SelectItem>
                <SelectItem value="Trousers">Trousers Only</SelectItem>
                <SelectItem value="Vest">Vest Only</SelectItem>
                <SelectItem value="Shirt">Shirt Only</SelectItem>
                <SelectItem value="Tuxedo">Tuxedo (Jacket + Trousers)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Step Navigation */}
      <section className="sticky top-16 z-50 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {[
              { id: 1, label: "GENERAL INFO", href: "/admin/mtm-order" },
              { id: 2, label: "FABRIC & LINING", href: "/admin/mtm-fabric" },
              { id: 3, label: "MEASUREMENTS", href: "/admin/mtm-measurements", active: true },
              { id: 4, label: "STYLES", href: "/admin/mtm-styles" },
              { id: 5, label: "SUBMIT", href: "/admin/mtm-submit" },
            ].map((step) => (
              <button
                key={step.id}
                onClick={() => navigate(step.href)}
                className={`
                  relative flex items-center gap-2 px-4 py-2 text-xs font-medium whitespace-nowrap transition-all
                  ${step.active 
                    ? "bg-primary text-primary-foreground" 
                    : step.id < 3 
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-slate-700 text-white hover:bg-slate-600"
                  }
                `}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step.id < 3 ? 'bg-white/30' : 'bg-white/20'}`}>
                  {step.id < 3 ? <Check size={12} /> : step.id}
                </span>
                {step.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Header */}
      <section className="py-4 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" onClick={() => navigate("/admin/mtm-order")}>
              <ArrowLeft size={16} className="mr-2" /> Back to Order
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{completionPercent}% Complete</span>
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${completionPercent}%` }} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Ruler className="text-primary" size={24} />
            <div>
              <h1 className="font-serif text-2xl">Measurements</h1>
              <p className="text-xs text-muted-foreground">Step 3 of 5 • Enter measurements for selected garments</p>
            </div>
          </div>
        </div>
      </section>

      {/* Garment Tabs */}
      <section className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 py-2">
            {/* Garment Type Selector */}
            <div className="flex items-center gap-2 mr-4 pr-4 border-r border-white/10">
              <span className="text-xs text-muted-foreground">Type:</span>
              <Select value={garmentType} onValueChange={handleGarmentTypeChange}>
                <SelectTrigger className="h-8 w-40 bg-background border-white/20 text-xs">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2-Piece Suit">2-Piece Suit</SelectItem>
                  <SelectItem value="3-Piece Suit">3-Piece Suit</SelectItem>
                  <SelectItem value="Sports Coat">Sports Coat</SelectItem>
                  <SelectItem value="Blazer">Blazer</SelectItem>
                  <SelectItem value="Overcoat">Overcoat</SelectItem>
                  <SelectItem value="Trousers">Trousers</SelectItem>
                  <SelectItem value="Vest">Vest</SelectItem>
                  <SelectItem value="Shirt">Shirt</SelectItem>
                  <SelectItem value="Tuxedo">Tuxedo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {includeJacket && (
              <button
                onClick={() => setActiveTab("jacket")}
                className={`px-4 py-2 text-sm font-medium rounded transition-all ${
                  activeTab === "jacket" ? "bg-blue-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Jacket {jacketModelSize && `(${jacketModelSize})`}
              </button>
            )}
            {includeTrouser && (
              <button
                onClick={() => setActiveTab("trouser")}
                className={`px-4 py-2 text-sm font-medium rounded transition-all ${
                  activeTab === "trouser" ? "bg-red-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Trousers {pantModelSize && `(${pantModelSize})`}
              </button>
            )}
            {includeVest && (
              <button
                onClick={() => setActiveTab("vest")}
                className={`px-4 py-2 text-sm font-medium rounded transition-all ${
                  activeTab === "vest" ? "bg-purple-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Vest {vestModelSize && `(${vestModelSize})`}
              </button>
            )}
            {includeShirt && (
              <button
                onClick={() => setActiveTab("shirt")}
                className={`px-4 py-2 text-sm font-medium rounded transition-all ${
                  activeTab === "shirt" ? "bg-green-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Shirt {shirtModelSize && `(${shirtModelSize})`}
              </button>
            )}
            
            {warnings.length > 0 && (
              <div className="ml-auto flex items-center gap-1 text-amber-400">
                <AlertTriangle size={14} />
                <span className="text-xs">{warnings.length} warning{warnings.length > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          
          {/* FIT PREFERENCES & BODY POSTURE - Always visible when garments selected */}
          {(includeJacket || includeTrouser || includeVest || includeShirt) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Fit Preferences */}
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-emerald-600 text-white px-4 py-2 font-medium text-sm flex items-center justify-between">
                  <span>FIT PREFERENCES</span>
                  <span className="text-xs opacity-75">Factory codes</span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    {/* Chest Fit */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Chest Fit</label>
                      <div className="space-y-1">
                        {FIT_PREFERENCES.chest.map(opt => (
                          <label key={opt.value} className={`flex items-center gap-2 p-2 rounded text-xs cursor-pointer transition-all ${fitPreferences.chest === opt.value ? 'bg-emerald-500/20 border border-emerald-500' : 'bg-muted/30 hover:bg-muted/50 border border-transparent'}`}>
                            <input type="radio" name="chestFit" value={opt.value} checked={fitPreferences.chest === opt.value} onChange={(e) => setFitPreferences(p => ({...p, chest: e.target.value}))} className="sr-only" />
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto font-mono">{opt.code}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Waist Suppression */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Waist Suppression</label>
                      <div className="space-y-1">
                        {FIT_PREFERENCES.waistSuppression.map(opt => (
                          <label key={opt.value} className={`flex items-center gap-2 p-2 rounded text-xs cursor-pointer transition-all ${fitPreferences.waistSuppression === opt.value ? 'bg-emerald-500/20 border border-emerald-500' : 'bg-muted/30 hover:bg-muted/50 border border-transparent'}`}>
                            <input type="radio" name="waistSuppression" value={opt.value} checked={fitPreferences.waistSuppression === opt.value} onChange={(e) => setFitPreferences(p => ({...p, waistSuppression: e.target.value}))} className="sr-only" />
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto font-mono">{opt.code}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Shoulder Style */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Shoulder Style</label>
                      <div className="space-y-1">
                        {FIT_PREFERENCES.shoulder.map(opt => (
                          <label key={opt.value} className={`flex items-center gap-2 p-2 rounded text-xs cursor-pointer transition-all ${fitPreferences.shoulder === opt.value ? 'bg-emerald-500/20 border border-emerald-500' : 'bg-muted/30 hover:bg-muted/50 border border-transparent'}`}>
                            <input type="radio" name="shoulder" value={opt.value} checked={fitPreferences.shoulder === opt.value} onChange={(e) => setFitPreferences(p => ({...p, shoulder: e.target.value}))} className="sr-only" />
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto font-mono">{opt.code}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Trouser Break */}
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Trouser Break</label>
                      <div className="space-y-1">
                        {FIT_PREFERENCES.trouserBreak.map(opt => (
                          <label key={opt.value} className={`flex items-center gap-2 p-2 rounded text-xs cursor-pointer transition-all ${fitPreferences.trouserBreak === opt.value ? 'bg-emerald-500/20 border border-emerald-500' : 'bg-muted/30 hover:bg-muted/50 border border-transparent'}`}>
                            <input type="radio" name="trouserBreak" value={opt.value} checked={fitPreferences.trouserBreak === opt.value} onChange={(e) => setFitPreferences(p => ({...p, trouserBreak: e.target.value}))} className="sr-only" />
                            <span>{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground ml-auto font-mono">{opt.code}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Factory Code Preview */}
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Factory Fit Code:</span>
                      <code className="text-xs bg-black/30 px-2 py-1 rounded font-mono text-emerald-400">
                        FIT={FIT_PREFERENCES.chest.find(f => f.value === fitPreferences.chest)?.code || 'CR'}-
                        {FIT_PREFERENCES.waistSuppression.find(f => f.value === fitPreferences.waistSuppression)?.code || 'WM'}-
                        {FIT_PREFERENCES.shoulder.find(f => f.value === fitPreferences.shoulder)?.code || 'SN'}-
                        {FIT_PREFERENCES.trouserBreak.find(f => f.value === fitPreferences.trouserBreak)?.code || 'BQ'}
                      </code>
                    </div>
                  </div>
                </div>
              </div>

              {/* Body Posture Notes */}
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-orange-600 text-white px-4 py-2 font-medium text-sm">BODY POSTURE NOTES</div>
                <div className="p-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Shoulder Slope</label>
                      <Select value={bodyPostureNotes.shoulderSlope} onValueChange={(v) => setBodyPostureNotes(p => ({...p, shoulderSlope: v}))}>
                        <SelectTrigger className="bg-background border-white/10 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="even">Even / Level</SelectItem>
                          <SelectItem value="left_high">Left Higher</SelectItem>
                          <SelectItem value="right_high">Right Higher</SelectItem>
                          <SelectItem value="sloping">Sloping (both)</SelectItem>
                          <SelectItem value="square">Square</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Stance / Posture</label>
                      <Select value={bodyPostureNotes.stance} onValueChange={(v) => setBodyPostureNotes(p => ({...p, stance: v}))}>
                        <SelectTrigger className="bg-background border-white/10 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="neutral">Neutral / Upright</SelectItem>
                          <SelectItem value="forward">Forward Lean (Stoop)</SelectItem>
                          <SelectItem value="back">Back Lean (Erect)</SelectItem>
                          <SelectItem value="forward_shoulder">Forward Shoulders</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-2">Seat Prominence</label>
                      <Select value={bodyPostureNotes.seatProminence} onValueChange={(v) => setBodyPostureNotes(p => ({...p, seatProminence: v}))}>
                        <SelectTrigger className="bg-background border-white/10 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="prominent">Prominent Seat</SelectItem>
                          <SelectItem value="flat">Flat Seat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* JACKET MEASUREMENTS */}
          {activeTab === "jacket" && includeJacket && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-4 py-3 font-medium">JACKET MEASUREMENTS</div>
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Model Size</label>
                      <Select value={jacketModelSize} onValueChange={applyJacketSize}>
                        <SelectTrigger className="w-28 h-9 bg-background border-white/20"><SelectValue placeholder="Select Size" /></SelectTrigger>
                        <SelectContent>{sizeCharts.jacket.map(s => <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-900/50 text-white text-xs">
                          <th className="py-2 px-3 text-left w-32">Measurement</th>
                          <th className="py-2 px-3 text-center w-24">Starting</th>
                          <th className="py-2 px-3 text-center w-28">Adjustment</th>
                          <th className="py-2 px-3 text-center w-24">Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        <MeasurementRow label="Chest" fieldKey="chest" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Stomach" fieldKey="stomach" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Seat" fieldKey="seat" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Shoulder" fieldKey="shoulder" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Bicep" fieldKey="bicep" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Sleeve Length" fieldKey="sleeve_length" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Back Length" fieldKey="back_length" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Front Length" fieldKey="front_length" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                        <MeasurementRow label="Cuff" fieldKey="cuff" startingSpecs={jacketStartingSpecs} adjustments={jacketAdjustments} setAdjustments={setJacketAdjustments} />
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Posture */}
                  <div className="mt-4">
                    <div className="bg-blue-900/50 text-white text-xs py-2 px-3 font-medium">POSTURE ADJUSTMENTS</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-muted/20 rounded-b-lg">
                      <PostureRow label="Stoop" fieldKey="stoop" posture={jacketPosture} setPosture={setJacketPosture} />
                      <PostureRow label="Erect" fieldKey="erect" posture={jacketPosture} setPosture={setJacketPosture} />
                      <PostureRow label="High Shoulder" fieldKey="high_shoulder" posture={jacketPosture} setPosture={setJacketPosture} />
                      <PostureRow label="Sloping Shoulder" fieldKey="sloping_shoulder" posture={jacketPosture} setPosture={setJacketPosture} />
                    </div>
                  </div>
                  
                  {/* Remarks */}
                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground block mb-1">Jacket Measurement Remarks</label>
                    <Textarea 
                      value={jacketRemarks} 
                      onChange={(e) => setJacketRemarks(e.target.value)} 
                      className="bg-background border-white/10" 
                      placeholder="Any special notes for jacket measurements..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* TROUSER MEASUREMENTS */}
          {activeTab === "trouser" && includeTrouser && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-red-700 text-white px-4 py-3 font-medium">TROUSER MEASUREMENTS</div>
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Model Size</label>
                      <Select value={pantModelSize} onValueChange={applyPantSize}>
                        <SelectTrigger className="w-28 h-9 bg-background border-white/20"><SelectValue placeholder="Select Size" /></SelectTrigger>
                        <SelectContent>{sizeCharts.pant.map(s => <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Model Type</label>
                      <Select value={pantModelType} onValueChange={setPantModelType}>
                        <SelectTrigger className="w-32 h-9 bg-background border-white/20"><SelectValue /></SelectTrigger>
                        <SelectContent>{PANT_MODEL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-red-900/50 text-white text-xs">
                          <th className="py-2 px-3 text-left w-32">Measurement</th>
                          <th className="py-2 px-3 text-center w-24">Starting</th>
                          <th className="py-2 px-3 text-center w-28">Adjustment</th>
                          <th className="py-2 px-3 text-center w-24">Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        <MeasurementRow label="Waist" fieldKey="waist" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Seat" fieldKey="seat" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Thigh" fieldKey="thigh" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Knee" fieldKey="knee" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Inseam" fieldKey="pant_length" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Rise" fieldKey="urise" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                        <MeasurementRow label="Bottom" fieldKey="bottom" startingSpecs={pantStartingSpecs} adjustments={pantAdjustments} setAdjustments={setPantAdjustments} />
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Posture */}
                  <div className="mt-4">
                    <div className="bg-red-900/50 text-white text-xs py-2 px-3 font-medium">POSTURE ADJUSTMENTS</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-muted/20 rounded-b-lg">
                      <PostureRow label="Prominent Seat" fieldKey="prominent_seat" posture={pantPosture} setPosture={setPantPosture} />
                      <PostureRow label="Flat Seat" fieldKey="flat_seat" posture={pantPosture} setPosture={setPantPosture} />
                      <PostureRow label="Bow Legs" fieldKey="bow_legs" posture={pantPosture} setPosture={setPantPosture} />
                      <PostureRow label="Knock Knees" fieldKey="knock_knees" posture={pantPosture} setPosture={setPantPosture} />
                    </div>
                  </div>
                  
                  {/* Remarks */}
                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground block mb-1">Trouser Measurement Remarks</label>
                    <Textarea 
                      value={pantRemarks} 
                      onChange={(e) => setPantRemarks(e.target.value)} 
                      className="bg-background border-white/10" 
                      placeholder="Any special notes for trouser measurements..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* VEST MEASUREMENTS */}
          {activeTab === "vest" && includeVest && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-purple-700 text-white px-4 py-3 font-medium">VEST MEASUREMENTS</div>
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Model Size</label>
                      <Select value={vestModelSize} onValueChange={applyVestSize}>
                        <SelectTrigger className="w-28 h-9 bg-background border-white/20"><SelectValue placeholder="Select Size" /></SelectTrigger>
                        <SelectContent>{sizeCharts.jacket.map(s => <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-purple-900/50 text-white text-xs">
                          <th className="py-2 px-3 text-left w-32">Measurement</th>
                          <th className="py-2 px-3 text-center w-24">Starting</th>
                          <th className="py-2 px-3 text-center w-28">Adjustment</th>
                          <th className="py-2 px-3 text-center w-24">Final</th>
                        </tr>
                      </thead>
                      <tbody>
                        <MeasurementRow label="Chest" fieldKey="chest" startingSpecs={vestStartingSpecs} adjustments={vestAdjustments} setAdjustments={setVestAdjustments} />
                        <MeasurementRow label="Stomach" fieldKey="stomach" startingSpecs={vestStartingSpecs} adjustments={vestAdjustments} setAdjustments={setVestAdjustments} />
                        <MeasurementRow label="Back Length" fieldKey="back_length" startingSpecs={vestStartingSpecs} adjustments={vestAdjustments} setAdjustments={setVestAdjustments} />
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Posture */}
                  <div className="mt-4">
                    <div className="bg-purple-900/50 text-white text-xs py-2 px-3 font-medium">POSTURE ADJUSTMENTS</div>
                    <div className="grid grid-cols-2 gap-4 p-3 bg-muted/20 rounded-b-lg">
                      <PostureRow label="Stoop" fieldKey="stoop" posture={vestPosture} setPosture={setVestPosture} />
                      <PostureRow label="Erect" fieldKey="erect" posture={vestPosture} setPosture={setVestPosture} />
                    </div>
                  </div>
                  
                  {/* Remarks */}
                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground block mb-1">Vest Measurement Remarks</label>
                    <Textarea 
                      value={vestRemarks} 
                      onChange={(e) => setVestRemarks(e.target.value)} 
                      className="bg-background border-white/10" 
                      placeholder="Any special notes for vest measurements..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* SHIRT MEASUREMENTS */}
          {activeTab === "shirt" && includeShirt && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
                <div className="bg-green-700 text-white px-4 py-3 font-medium">SHIRT MEASUREMENTS</div>
                <div className="p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Model Size</label>
                      <Select value={shirtModelSize} onValueChange={(val) => {
                        setShirtModelSize(val);
                        const chart = sizeCharts.shirt?.find(s => s.size === val);
                        if (chart) {
                          setShirtStartingSpecs({
                            neck: chart.neck || 0,
                            chest: chart.chest || 0,
                            stomach: chart.stomach || 0,
                            seat: chart.seat || 0,
                            shoulder: chart.shoulder || chart.shoulders || 0,
                            sleeve_length: chart.sleeve_length || 0,
                            back_length: chart.back_length || chart.shirt_length || 0,
                            cuff: chart.cuff || 0,
                          });
                          setShirtAdjustments({
                            neck: 0, chest: 0, stomach: 0, seat: 0, shoulder: 0,
                            right_sleeve_length: 0, left_sleeve_length: 0,
                            back_length: 0, right_cuff: 0, left_cuff: 0,
                          });
                        }
                      }}>
                        <SelectTrigger className="w-32 bg-background border-white/20">
                          <SelectValue placeholder="Select Size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeCharts.shirt?.map(s => (
                            <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {shirtModelSize && (
                    <div className="border border-white/10 rounded overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Measurement</th>
                            <th className="px-3 py-2 text-center font-medium">Starting</th>
                            <th className="px-3 py-2 text-center font-medium">Adjustment</th>
                            <th className="px-3 py-2 text-center font-medium">Final</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          <MeasurementRow label="Neck" fieldKey="neck" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Chest" fieldKey="chest" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Stomach" fieldKey="stomach" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Seat" fieldKey="seat" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Shoulder" fieldKey="shoulder" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Right Sleeve Length" fieldKey="right_sleeve_length" startingSpecs={{...shirtStartingSpecs, right_sleeve_length: shirtStartingSpecs.sleeve_length}} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Left Sleeve Length" fieldKey="left_sleeve_length" startingSpecs={{...shirtStartingSpecs, left_sleeve_length: shirtStartingSpecs.sleeve_length}} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Back Length" fieldKey="back_length" startingSpecs={shirtStartingSpecs} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Right Cuff" fieldKey="right_cuff" startingSpecs={{...shirtStartingSpecs, right_cuff: shirtStartingSpecs.cuff}} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                          <MeasurementRow label="Left Cuff" fieldKey="left_cuff" startingSpecs={{...shirtStartingSpecs, left_cuff: shirtStartingSpecs.cuff}} adjustments={shirtAdjustments} setAdjustments={setShirtAdjustments} />
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Remarks */}
                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground block mb-1">Remarks</label>
                    <Textarea 
                      value={shirtRemarks} 
                      onChange={(e) => setShirtRemarks(e.target.value)} 
                      className="bg-background border-white/10" 
                      placeholder="Any special notes for shirt measurements..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
            <Button variant="outline" onClick={() => navigate("/admin/mtm-fabric")}>
              <ArrowLeft size={16} className="mr-2" /> Back to Fabric
            </Button>
            <Button onClick={handleSaveAndContinue} disabled={saving}>
              {saving ? "Saving..." : <>Save & Continue <ChevronRight size={16} className="ml-2" /></>}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminMTMMeasurementsPage;
